import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Button, Drawer, InputGroup, Select, Tooltip, toastManager } from '@systemeio/components'
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  Save01Icon,
  Trash02Icon,
  XCloseIcon,
} from '@systemeio/icons'
import { cn } from 'cn'
import { useTranslation } from 'next-i18next/pages'
import type { SubmitHandler } from 'react-hook-form'
import { Controller, useForm } from 'react-hook-form'
import type { ThemeInterface } from 'themes/types'
import { themeColorNames } from 'themes/types'
import { applyTheme } from 'themes/utils'
import { z } from 'zod'
import { BadRequest } from 'shared/errors/bad-request'
import { useCourseData } from 'modules/course/hooks/use-course-data'
import type { CourseDataInterface } from 'modules/course/types/course-interface'
import useThemeActions from '../hooks/use-theme-actions'
import useThemeEditorToggle from '../hooks/use-theme-editor-toggle'
import useThemes from '../hooks/use-themes'
import type { CourseThemeInterface } from '../types/theme-interface'
import { normalizeErrorColorFieldName } from '../utils/normalize-color-set'
import { getCustomThemeDefaultName } from '../utils/theme-number'
import ThemeEditorColorField from './color-field'
import CourseEditorSkeleton from './theme-editor-skeleton'

type ThemeEditorFormInterface = ThemeInterface & { name: string; themeId: number }

const CUSTOM_THEME_STAB_ID = -1

export default function ThemeEditor(): ReactNode {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)

  const [initialTheme, setInitialTheme] = useState<CourseThemeInterface>()
  const [collapsed, setCollapsed] = useState(false)

  const { isOpenEditor, allowedToEdit, closeEditor } = useThemeEditorToggle()

  const { courseData, mutate: mutateCourseData, isValidating } = useCourseData()

  const { themes, mutate, isLoading } = useThemes({ allowFetch: isOpenEditor })
  const {
    createTheme,
    applyThemeToCourse,
    deleteTheme: deleteThemeForOwner,
    updateTheme,
  } = useThemeActions()

  const themeOptions = [
    { id: CUSTOM_THEME_STAB_ID, caption: t('themes.editor.add_custom_theme') },
    ...(themes?.map(theme => ({ id: theme.id, caption: theme.name })) ?? []),
  ]

  const { handleSubmit, control, setValue, reset, watch, getValues, setError, formState } =
    useForm<ThemeEditorFormInterface>()

  const revalidateCourseData = async (): Promise<CourseDataInterface | undefined> =>
    mutateCourseData(data => data && { ...data })

  const currentTheme = themes?.find(theme => theme.id === getValues('themeId'))

  const isCustomTheme = !!currentTheme?.user

  const applyThemeToCourseWithUpdate = async (themeId: number): Promise<void> => {
    const updatedCourseData = await applyThemeToCourse(themeId)
    await mutateCourseData(
      courseData =>
        courseData && {
          ...courseData,
          themeId: updatedCourseData.themeId,
        },
    )

    toastManager.add({ type: 'success', title: t('themes.editor.message.success') })
  }

  const onSubmit: SubmitHandler<ThemeEditorFormInterface> = async data => {
    let { themeId } = data

    if (themeId && (themeId === CUSTOM_THEME_STAB_ID || isCustomTheme)) {
      try {
        const isUpdating = themeId !== CUSTOM_THEME_STAB_ID && isCustomTheme
        const theme = isUpdating
          ? await updateTheme(themeId, { name: data.name, colorSet: { ...data } })
          : await createTheme({ name: data.name, colorSet: { ...data } })
        await mutate(themes => (themes ? [...themes, theme] : [theme]))
        themeId = theme?.id
      } catch (error) {
        if (error instanceof BadRequest) {
          if (error?.errors?.common) {
            const errorToShow = error.errors.common.join('/n')
            setError('root', { message: errorToShow })
          }

          if (error?.errors?.fields) {
            const fields = z
              .record(
                z.string(),
                z.union([z.array(z.string()), z.record(z.string(), z.array(z.string()))]),
              )
              .safeParse(error.errors.fields)
            if (!fields.success) return

            for (const [key, value] of Object.entries(fields.data)) {
              if (key === 'colorSet') {
                if (Array.isArray(value)) continue
                for (const [color, colorErrors] of Object.entries(value)) {
                  const name = themeColorNames.find(
                    name => name === normalizeErrorColorFieldName(color),
                  )
                  if (name) setError(name, { message: colorErrors.join('') })
                }
                continue
              }

              const name = [...themeColorNames, 'name', 'themeId'] as const
              const field = name.find(name => name === key)
              if (field && Array.isArray(value)) setError(field, { message: value.join('\n') })
            }
          }
        }

        return
      }
    }

    await applyThemeToCourseWithUpdate(themeId)
  }

  const handleThemeChange = (value: number): void => {
    const selectedTheme = themes?.find(theme => theme.id === value)

    if (selectedTheme) {
      reset({ ...selectedTheme.colorSet, name: selectedTheme.name, themeId: value })
      applyTheme(selectedTheme.colorSet)
    }
  }

  const handleColorChange =
    (colorName: keyof ThemeInterface) =>
    (color: number | string | null | undefined): void => {
      if (!isCustomTheme) {
        setValue('themeId', -1)
      }

      applyTheme({ ...getValues(), [colorName]: color })
    }

  const handleClose = (): void => {
    setCollapsed(false)
    if (courseData?.themeId !== getValues('themeId') && courseData?.colorSet) {
      applyTheme(courseData.colorSet)
    }

    closeEditor()
  }

  const deleteTheme = async (): Promise<void> => {
    const themeId = currentTheme?.id

    if (!themeId) return

    await deleteThemeForOwner(themeId)
    await mutate(data => data?.filter(theme => theme.id !== themeId))

    if (themeId === courseData?.themeId) {
      await revalidateCourseData()
    }

    if (courseData) {
      setValue('themeId', courseData.themeId)
      if (courseData.colorSet) {
        applyTheme(courseData.colorSet)
      }
    }

    if (themeId === initialTheme?.id) {
      setInitialTheme(undefined)
    }
  }

  const handleReset = async (): Promise<void> => {
    if (!initialTheme) return

    if (courseData?.themeId !== initialTheme.id) {
      await applyThemeToCourseWithUpdate(initialTheme.id)
      return
    }

    reset({ ...initialTheme.colorSet, name: initialTheme.name, themeId: initialTheme.id })
    applyTheme(initialTheme.colorSet)
  }

  const canReset = initialTheme?.id !== currentTheme?.id

  useEffect(() => {
    if (!allowedToEdit && isOpenEditor) closeEditor()
  }, [allowedToEdit, isOpenEditor])

  useEffect(() => {
    if (courseData) {
      const current = themes?.find(theme => theme.id === courseData.themeId)
      reset({
        ...courseData.colorSet,
        themeId: courseData.themeId,
        name: current?.name,
      })
    }
  }, [courseData?.themeId, courseData?.colorSet])

  const themeId = watch('themeId')

  useEffect(() => {
    if (themeId === CUSTOM_THEME_STAB_ID) {
      setValue('name', getCustomThemeDefaultName(t('themes.editor.defaults.name'), themes))
    }
  }, [themeId])

  useEffect(() => {
    if (!initialTheme && currentTheme) {
      setInitialTheme(currentTheme)
    }
  }, [currentTheme, initialTheme])

  const content =
    isValidating || isLoading ? (
      <CourseEditorSkeleton />
    ) : (
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        <div className="flex gap-3">
          <Controller
            control={control}
            name="themeId"
            render={({ field }) => (
              <Select.Root
                items={themeOptions.map(option => ({
                  value: option.id,
                  label: option.caption,
                }))}
                value={field.value ?? null}
                onValueChange={value => {
                  if (value === null) return
                  setValue('themeId', value)
                  handleThemeChange(value)
                }}
              >
                <Select.Trigger
                  size="md"
                  className="max-w-full grow px-3 py-2.5"
                  data-testid="theme-editor-theme-select"
                >
                  <Select.Value placeholder={t('global.select')} />
                  <Select.Icon className="text-text-primary">
                    <ChevronDownIcon className="size-5" />
                  </Select.Icon>
                </Select.Trigger>
                <Select.Content className="shadow-2">
                  <Select.List>
                    {themeOptions.map(option => (
                      <Select.Item key={option.id} value={option.id}>
                        <Select.ItemText>{option.caption}</Select.ItemText>
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.List>
                </Select.Content>
              </Select.Root>
            )}
          />
          {isCustomTheme && (
            <Tooltip
              trigger={
                <Button variant="secondary" size="icon-md" onClick={deleteTheme} type="button">
                  <Trash02Icon className="size-5" />
                </Button>
              }
              content={
                <span
                  dangerouslySetInnerHTML={{
                    __html: t('themes.editor.delete.tooltip'),
                  }}
                />
              }
            />
          )}
        </div>
        <div className="flex flex-col gap-2 px-2">
          {themeColorNames.map(color => (
            <ThemeEditorColorField
              key={color}
              name={color}
              control={control}
              afterChange={handleColorChange(color)}
              setValue={setValue}
              error={formState.errors[color]?.message}
            />
          ))}
        </div>
        {(watch('themeId') === CUSTOM_THEME_STAB_ID || isCustomTheme) && (
          <Controller
            control={control}
            name="name"
            rules={{ required: t('themes.editor.validation.name.required') }}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <label className="flex gap-0.5 text-sm font-medium text-text-primary">
                  {t('global.name')}
                  <span className="text-text-error">*</span>
                </label>
                <InputGroup.Root data-invalid={!!formState.errors.name || undefined}>
                  <InputGroup.Input
                    placeholder={t('themes.editor.name.placeholder')}
                    value={field.value ?? ''}
                    onChange={e => {
                      setValue('name', e.target.value)
                    }}
                  />
                </InputGroup.Root>
                {formState.errors.name && (
                  <p className="text-sm text-text-error">{formState.errors.name.message}</p>
                )}
              </div>
            )}
          />
        )}
        {formState.errors.root && (
          <p className="mt-2 text-sm text-text-error">{formState.errors.root.message}</p>
        )}
        <Button variant="primary" type="submit">
          <Save01Icon className="size-5" /> {t('themes.editor.cta.save_theme_settings')}
        </Button>
        {canReset && (
          <Button variant="secondary" type="reset" onClick={handleReset}>
            {t('themes.editor.cta.revert_theme_settings')}
          </Button>
        )}
      </form>
    )

  return (
    <div ref={containerRef}>
      {isOpenEditor && (
        <button
          type="button"
          className={cn(
            'fixed top-1/2 z-50 -translate-y-1/2',
            collapsed ? 'right-0' : 'right-80 md:right-96',
          )}
          aria-label={t('themes.editor.cta.manage_themes')}
          aria-expanded={!collapsed}
          onClick={() => setCollapsed(!collapsed)}
        >
          <ChevronLeftIcon className="size-5" />
        </button>
      )}
      <Drawer.Root
        side="right"
        open={isOpenEditor && !collapsed}
        modal={false}
        disablePointerDismissal
        onOpenChange={(open, details) => {
          if (open) return
          if (details.reason === 'swipe') setCollapsed(true)
          else handleClose()
        }}
      >
        <Drawer.Popup
          data-testid="drawer"
          className="pointer-events-auto w-80 text-inherit md:w-96"
          backdropProps={{ className: 'pointer-events-none bg-transparent backdrop-blur-none' }}
          viewportProps={{ className: 'pointer-events-none' }}
          portalProps={{ container: containerRef }}
        >
          <Drawer.Content className="customScroll gap-5 p-5">
            <Drawer.Header className="grid grid-cols-[1fr_10%] items-center border-0 p-0">
              <Drawer.Title className="lg:text-3xl">{t('themes.editor.title')}</Drawer.Title>
              <Drawer.Close aria-label={t('global.close')} className="justify-self-end">
                <XCloseIcon className="size-5" />
              </Drawer.Close>
            </Drawer.Header>
            {content}
          </Drawer.Content>
        </Drawer.Popup>
      </Drawer.Root>
    </div>
  )
}
